import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { ResultsService } from 'src/app/services/results.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';
import { AuthorizationByStatusModel } from 'src/app/shared/models/authorization-model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ness-results-detail',
  templateUrl: './results-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResultsDetailComponent implements OnDestroy, OnChanges {
  apiRoot: string = environment.apiEndpoint;
  canEdit: AuthorizationByStatusModel;
  displayErrorStatus: boolean = false;
  errorMessage: string = 'An error occurred.';

  private _resultData: ServerModels.ResultDetailViewModel = null;
  resultData$: BehaviorSubject<ServerModels.ResultDetailViewModel> = new BehaviorSubject(this._resultData);

  private _resultId: string;
  @Input() set resultId(id: string) {
    this._resultId = id;
  }

  get hasPrimaryCrimeGunID() {
    return !this._resultData?.ENESS_SuccessStory?.ENESS_XREF_SuccessStoriesCrimeGuns?.find(p => p.IsPrimary);
  }

  get hasLinkedCrimeGunID() {
    return !this._resultData?.ENESS_SuccessStory?.ENESS_XREF_SuccessStoriesCrimeGuns?.find(p => !p.IsPrimary);
  }

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private resultService: ResultsService,
    private authService: AuthService,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    if (this.route.component['name'] === 'ResultsDetailComponent' && !!this.route.snapshot.params['id']) {
      this._resultId = this.route.snapshot.params['id'];
      this.loadResult();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['resultId'] && changes['resultId'].currentValue != changes['resultId'].previousValue && changes['resultId'].toString() != this._resultId) {
      this.loadResult();
    }
  }

  public approveResult(): void {
    if (!this._resultData.CurrentUserIsApprover && !this._resultData.CurrentUserIsFDLeadership) {
      return;
    }
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['result-form', this._resultData.ENESS_SuccessStory.SuccessStoryID], { queryParams: { tabIndex: 4 } });
  }

  public editResult(): void {
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['/result-form/' + this._resultData.ENESS_SuccessStory.SuccessStoryID])
  }

  public loadResult(): void {
    if (!this._resultId) {
      return;
    }

    this.resultService.getResultById(this._resultId).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (result: ServerModels.ResultDetailViewModel) => {
        this._resultData = result;
        this.resultData$.next(this._resultData);
        this.canEdit = {
          role: ['NESS Administrator'],
          creatorId: result.ENESS_SuccessStory.CreatedBy_Id,
          supervisorId: result.ENESS_SuccessStory.CGICSupervisor_Id,
          alternateApproverId: result.ENESS_SuccessStory.AlternateCGICApprover_Id,
          isFieldDivisionLeadership: result.CurrentUserIsFDLeadership,
          status: result.ENESS_SuccessStory.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus,
          userType: [],
          entityState: AppConstants.Result_FormMode_Edit,
          roleUserType: [
            {
              roles: ['Application Administrator', 'Power User', 'Editor'],
              userType: 'ATFFieldDivision'
            },
            {
              roles: ['Application Administrator', 'Editor'],
              userType: 'StateLocalLE'
            }
          ]
        } as AuthorizationByStatusModel;
      }
    );
  }

  public canApprove(): boolean {
    if (this._resultData?.ENESS_SuccessStory?.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus == AppConstants.Result_Status_Pending1stLineApproval) {
      return this._resultData.CurrentUserIsApprover || this.authService.hasRole(['NESS Administrator']);
    }

    if (this._resultData?.ENESS_SuccessStory?.CurrentSuccessStoryStatus?.ENESS_REF_SuccessStoriesStatuses?.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval) {
      return this._resultData.CurrentUserIsFDLeadership || this.authService.hasRole(['NESS Administrator']);
    }

    return false;
  }

  public onClickDownload(file: ServerModels.ENESS_SuccessStoryAttachments) {
    if (file) {
      this.errorMessage = '';
      this.displayErrorStatus = false;
      this.resultService.getAttachmentById(this._resultId, file.SuccessStoryAttachmentID).subscribe(
        (attachment:Blob) => {
          AppUtils.embedDownloadLink(attachment, file.FileName);
        },
        (err:any) => {
          this.errorMessage = `${err} Unable to retrieve requested attachment.`;
          this.displayErrorStatus = true;
        }
      );
    }
  }

  public onClickDownloadResult(): void {
    this.resultService.downloadPrintablePdf(this._resultId).pipe(take(1)).subscribe((data:Blob) => {
      const pending2ndLineApprovalStatus = this._resultData.SuccessStoriesStatuses.find(x => x.SuccessStoriesStatus == AppConstants.Result_Status_Pending2ndLineApproval);

      let fileName = this._resultData.ENESS_SuccessStory.CurrentSuccessStoryStatus.StatusID.toLowerCase() === pending2ndLineApprovalStatus.SuccessStoriesStatusID.toLowerCase() ? this._resultData.ENESS_SuccessStory.InvestigativeTrackingNumber + "_" + new DatePipe('en-US').transform(new Date(), "MMddyyyy") + "_Draft.pdf"
        : this._resultData.ENESS_SuccessStory.InvestigativeTrackingNumber + "_" + new DatePipe('en-US').transform(new Date(), "MMddyyyy") + ".pdf";

      AppUtils.embedDownloadLink(data, fileName);
    },
      (err:any) => {
        this.errorMessage = `${err} Unable to retrieve requested PDF.`;
        this.displayErrorStatus = true;
      });
  }

  public openRelatedCrimeGun(evt: Event, id: string): void {
    evt.preventDefault();
    const token = sessionStorage.getItem(environment.authTokenStorageKey);
    let url = (<HTMLLinkElement>evt.target).baseURI + '/crimegun/' + id;
    let windowObjRef = window.open(url, '_blank');
    windowObjRef.sessionStorage.setItem(environment.authTokenStorageKey, token);
  }
}
